import type { EChartsCoreOption } from 'echarts/core';

import { CHART_COLORS, STATUS_META, STATUS_ORDER, TRevenueMode } from './constants';
import {
  bucketLabel,
  formatAxisMoney,
  formatMoney,
  formatNumber,
  formatPercent,
} from './utils';
import {
  TCrmCohortRow,
  TCrmGranularity,
  TCrmSummaryRow,
} from '../../../shared/common/api/endpoints/crmAnalyticsApi';

export type TChartOption = EChartsCoreOption;

interface TSeriesDef {
  name: string;
  color: string;
  values: (number | null)[];
}

interface TTooltipParam {
  seriesName: string;
  value: number | null | { value: number | null };
  color: string;
  dataIndex: number;
  axisValueLabel: string;
}

const RADIUS = 4;

const paramValue = (param: TTooltipParam): number | null => {
  const { value } = param;
  if (typeof value === 'object') return value === null ? null : value.value;

  return value;
};

const GRID = { left: 8, right: 16, top: 44, bottom: 8, containLabel: true };

// Один ряд — легенда не нужна, его называет заголовок графика
const withoutLegend = (option: TChartOption): TChartOption => ({
  ...option,
  legend: { show: false },
  grid: { ...GRID, top: 16 },
});

const dot = (color: string): string =>
  `<span style="display:inline-block;width:8px;height:8px;border-radius:2px;margin-right:6px;background:${color}"></span>`;

const tooltipRow = (color: string | null, label: string, value: string, muted = false): string =>
  `<div style="display:flex;justify-content:space-between;gap:16px;color:${muted ? CHART_COLORS.tooltipMuted : CHART_COLORS.tooltipText}">`
  + `<span>${color ? dot(color) : ''}${label}</span><span style="font-variant-numeric:tabular-nums">${value}</span></div>`;

const tooltipTitle = (title: string): string =>
  `<div style="margin-bottom:4px;font-weight:600;color:${CHART_COLORS.tooltipText}">${title}</div>`;

const baseOption = (labels: string[], valueFormatter: (value: number) => string): TChartOption => ({
  animation: false,
  grid: GRID,
  legend: {
    top: 0,
    left: 0,
    icon: 'roundRect',
    itemWidth: 10,
    itemHeight: 10,
    itemGap: 16,
    textStyle: { color: '#595959' },
  },
  xAxis: {
    type: 'category',
    data: labels,
    axisTick: { show: false },
    axisLine: { lineStyle: { color: CHART_COLORS.axisLine } },
    axisLabel: { color: CHART_COLORS.axisText, hideOverlap: true },
  },
  yAxis: {
    type: 'value',
    splitLine: { lineStyle: { color: CHART_COLORS.gridLine } },
    axisLabel: { color: CHART_COLORS.axisText, formatter: valueFormatter },
  },
});

const axisTooltip = (formatter: (params: TTooltipParam[]) => string): TChartOption => ({
  trigger: 'axis',
  axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(0, 0, 0, 0.04)' } },
  backgroundColor: '#ffffff',
  borderColor: '#f0f0f0',
  extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.08); min-width: 200px;',
  formatter,
});

/**
 * Столбцы в стопке: скругляется только верхний непустой сегмент (конец столбца, а не каждый кусок),
 * между сегментами — щель цвета фона. Нули не рисуются: иначе обводка оставляет волосок у оси
 */
const stackedBars = (defs: TSeriesDef[], stack: string, negative = false): TChartOption[] => {
  const count = defs[0]?.values.length ?? 0;
  const topIndex = Array.from({ length: count }, (_, i) => {
    let top = -1;
    defs.forEach((def, k) => {
      if ((def.values[i] ?? 0) !== 0) top = k;
    });
    return top;
  });

  return defs.map((def, k) => ({
    name: def.name,
    type: 'bar',
    stack,
    barMaxWidth: 32,
    itemStyle: { color: def.color, borderColor: CHART_COLORS.surface, borderWidth: 1 },
    emphasis: { focus: 'series' },
    data: def.values.map((value, i) => ({
      value: value === 0 ? null : value,
      itemStyle: topIndex[i] === k
        ? { borderRadius: negative ? [ 0, 0, RADIUS, RADIUS ] : [ RADIUS, RADIUS, 0, 0 ] }
        : undefined,
    })),
  }));
};

const labelsOf = (rows: { bucket: string }[], granularity: TCrmGranularity): string[] =>
  rows.map((row) => bucketLabel(row.bucket, granularity));

/**
 * Школы по статусам на последний день каждого отрезка
 */
export const statusChartOption = (
  rows: TCrmSummaryRow[],
  granularity: TCrmGranularity,
  showDead: boolean,
): TChartOption => {
  const statuses = STATUS_ORDER.filter((status) => showDead || status !== 'grace_dead');
  const defs = statuses.map((status) => ({
    name: STATUS_META[status].label,
    color: STATUS_META[status].color,
    values: rows.map((row) => (row.statuses ? row.statuses[status] : null)),
  }));

  return {
    ...baseOption(labelsOf(rows, granularity), formatNumber),
    tooltip: axisTooltip((params) => {
      const row = rows[params[0]?.dataIndex ?? 0];
      if (!row || !row.statuses) return '';

      const total = params.reduce((sum, param) => sum + (paramValue(param) ?? 0), 0);
      return tooltipTitle(params[0].axisValueLabel)
        + params.slice().reverse().map((param) => tooltipRow(param.color, param.seriesName, formatNumber(paramValue(param) ?? 0))).join('')
        + tooltipRow(null, 'Всего', formatNumber(total))
        + tooltipRow(null, `на ${row.snapshot_day?.split('-').reverse().join('.')}`, '', true);
    }),
    series: stackedBars(defs, 'statuses'),
  };
};

/**
 * Доход по отрезкам: поступления или начислено; без фильтра «только реальные» — стопка с бонусами и исключёнными
 */
export const revenueChartOption = (
  rows: TCrmSummaryRow[],
  granularity: TCrmGranularity,
  mode: TRevenueMode,
  onlyReal: boolean,
): TChartOption => {
  const defs: TSeriesDef[] = mode === 'cash'
    ? [
      { name: 'Реальные платежи', color: CHART_COLORS.primary, values: rows.map((row) => row.cash_real) },
      ...(onlyReal ? [] : [
        { name: 'Бонусные пополнения', color: CHART_COLORS.secondary, values: rows.map((row) => row.cash_bonus) },
        { name: '«Не учитывать» и возвраты', color: CHART_COLORS.tertiary, values: rows.map((row) => row.cash_excluded) },
      ]),
    ]
    : [
      { name: 'С основного счёта', color: CHART_COLORS.primary, values: rows.map((row) => row.accrual_real) },
      ...(onlyReal ? [] : [
        { name: 'С бонусного счёта', color: CHART_COLORS.secondary, values: rows.map((row) => row.accrual_bonus) },
      ]),
    ];

  const option: TChartOption = {
    ...baseOption(labelsOf(rows, granularity), formatAxisMoney),
    tooltip: axisTooltip((params) => {
      const row = rows[params[0]?.dataIndex ?? 0];
      if (!row) return '';

      const total = params.reduce((sum, param) => sum + (paramValue(param) ?? 0), 0);
      const extra = mode === 'cash'
        ? tooltipRow(null, 'Платили школ', formatNumber(row.payers), true)
        : tooltipRow(null, 'Школ с оплаченным доступом', formatNumber(row.paying_schools), true);

      return tooltipTitle(params[0].axisValueLabel)
        + params.slice().reverse().map((param) => tooltipRow(param.color, param.seriesName, formatMoney(paramValue(param) ?? 0))).join('')
        + (params.length > 1 ? tooltipRow(null, 'Всего', formatMoney(total)) : '')
        + extra;
    }),
    series: stackedBars(defs, 'revenue'),
  };

  return defs.length > 1 ? option : withoutLegend(option);
};

export const commissionChartOption = (rows: TCrmSummaryRow[], granularity: TCrmGranularity): TChartOption =>
  withoutLegend({
    ...baseOption(labelsOf(rows, granularity), formatAxisMoney),
    tooltip: axisTooltip((params) => {
      const row = rows[params[0]?.dataIndex ?? 0];
      if (!row) return '';

      const share = row.cash_real > 0 ? row.partner_commission / row.cash_real * 100 : null;
      return tooltipTitle(params[0].axisValueLabel)
        + tooltipRow(CHART_COLORS.commission, 'Комиссия партнёров', formatMoney(row.partner_commission))
        + tooltipRow(null, 'от реальных поступлений', formatPercent(share === null ? null : Math.round(share * 10) / 10), true);
    }),
    series: stackedBars([
      { name: 'Комиссия партнёров', color: CHART_COLORS.commission, values: rows.map((row) => row.partner_commission) },
    ], 'commission'),
  });

/**
 * Движение платящих школ: вверх — начали платить и вернулись, вниз — ушли
 */
export const movementChartOption = (rows: TCrmSummaryRow[], granularity: TCrmGranularity): TChartOption => {
  const gains = stackedBars([
    { name: 'Начали платить', color: CHART_COLORS.gain, values: rows.map((row) => row.new_paying) },
    { name: 'Вернулись', color: CHART_COLORS.gainLight, values: rows.map((row) => row.returned) },
  ], 'in');
  const losses = stackedBars([
    { name: 'Ушли', color: CHART_COLORS.loss, values: rows.map((row) => (row.churned ? -row.churned : 0)) },
  ], 'out', true);

  return {
    ...baseOption(labelsOf(rows, granularity), (value: number) => formatNumber(Math.abs(value))),
    tooltip: axisTooltip((params) => {
      const row = rows[params[0]?.dataIndex ?? 0];
      if (!row) return '';

      return tooltipTitle(params[0].axisValueLabel)
        + tooltipRow(CHART_COLORS.gain, 'Начали платить', formatNumber(row.new_paying))
        + tooltipRow(CHART_COLORS.gainLight, 'Вернулись', formatNumber(row.returned))
        + tooltipRow(CHART_COLORS.loss, row.churn_final ? 'Ушли' : 'Ушли (предварительно)', formatNumber(row.churned))
        + tooltipRow(null, 'перестали платить', formatNumber(row.churned_stopped), true)
        + tooltipRow(null, 'выручка упала → льготный', formatNumber(row.churned_grace), true)
        + tooltipRow(null, 'Платили на начало', formatNumber(row.paying_start));
    }),
    series: [ ...gains, ...losses ],
  };
};

/**
 * Доля ставших покупателями за 14 / 30 / 90 дней с подключения; незавершённые окна не рисуются
 */
export const cohortChartOption = (rows: TCrmCohortRow[], granularity: TCrmGranularity): TChartOption => {
  const windows = rows[0]?.windows.map((window) => window.days) ?? [];

  return {
    ...baseOption(labelsOf(rows, granularity), (value: number) => `${value} %`),
    yAxis: {
      type: 'value',
      max: 100,
      splitLine: { lineStyle: { color: CHART_COLORS.gridLine } },
      axisLabel: { color: CHART_COLORS.axisText, formatter: (value: number) => `${value} %` },
    },
    tooltip: axisTooltip((params) => {
      const row = rows[params[0]?.dataIndex ?? 0];
      if (!row) return '';

      return tooltipTitle(`Подключились: ${params[0].axisValueLabel}`)
        + tooltipRow(null, 'Подключились', formatNumber(row.connected))
        + row.windows.map((window, i) => tooltipRow(
          CHART_COLORS.windows[i],
          `Оплатили за ${window.days} дн.`,
          window.mature ? `${formatNumber(window.paid)} · ${formatPercent(window.conversion)}` : 'окно не прошло',
        )).join('')
        + tooltipRow(null, 'За всё время', `${formatNumber(row.paid_ever)} · ${formatPercent(row.conversion)}`);
    }),
    series: windows.map((days, i) => ({
      name: `За ${days} дн.`,
      type: 'bar',
      barMaxWidth: 14,
      barGap: '20%',
      itemStyle: { color: CHART_COLORS.windows[i], borderRadius: [ RADIUS, RADIUS, 0, 0 ] },
      data: rows.map((row) => {
        const window = row.windows[i];
        return window && window.mature && row.connected > 0 ? window.conversion : null;
      }),
    })),
  };
};
